import { apiGet, apiGetResponse, apiPost, apiDelete, apiPatch } from '../lib/api-client';
import type {
  LiveClass,
  LiveClassDetail,
  LiveClassListResponse,
  LiveClassSession,
  RegisterSessionResponse,
  RegisterAllResponse,
  MyLiveClass,
  JoinTokenResponse,
  SessionStartResponse,
  SessionEndResponse,
  LiveClassMessage,
  MessagePageResponse,
  PollResults,
} from '@edytech/shared/types';
import type { CreateMessageInput, CreatePollInput } from '@edytech/shared/validators';

export interface LiveClassQueryParams {
  status?: string;
  courseId?: number;
  instructorId?: number;
  q?: string;
  page?: number;
  limit?: number;
}

export interface UpdateLiveClassInput {
  title?: string;
  description?: string | null;
  tags?: string[];
  capacity?: number;
  isPublished?: boolean;
}

export interface UpdateSessionInput {
  title?: string | null;
  startsAt?: string;
  endsAt?: string;
  timezone?: string;
  capacity?: number | null;
}

export const liveClassService = {
  /** Admin sees all classes; students see published classes with upcoming sessions. */
  async list(params?: LiveClassQueryParams): Promise<LiveClassListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.courseId) query.append('courseId', String(params.courseId));
    if (params?.instructorId) query.append('instructorId', String(params.instructorId));
    if (params?.q) query.append('q', params.q);
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    // The API returns `{ success, data: LiveClass[], pagination }` at the top level;
    // use the raw response so callers get both the items and pagination info.
    return apiGetResponse<LiveClassListResponse>(`/live-classes${qs ? `?${qs}` : ''}`);
  },

  async getById(idOrSlug: string | number): Promise<LiveClassDetail> {
    return apiGet<LiveClassDetail>(`/live-classes/${idOrSlug}`);
  },

  async getUpcoming(idOrSlug: string | number): Promise<LiveClassSession[]> {
    return apiGet<LiveClassSession[]>(`/live-classes/${idOrSlug}/upcoming`);
  },

  async register(sessionId: number): Promise<RegisterSessionResponse> {
    return apiPost<RegisterSessionResponse>(`/live-classes/sessions/${sessionId}/register`);
  },

  async cancelRegistration(sessionId: number): Promise<{ id: number; status: string }> {
    return apiDelete<{ id: number; status: string }>(`/live-classes/sessions/${sessionId}/register`);
  },

  async registerAll(classId: number): Promise<RegisterAllResponse> {
    return apiPost<RegisterAllResponse>(`/live-classes/${classId}/register-all`);
  },

  async getMyLiveClasses(scope: 'upcoming' | 'past' | 'recorded'): Promise<MyLiveClass[]> {
    return apiGet<MyLiveClass[]>('/me/live-classes', { scope });
  },

  // ---- Admin (used by admin-web) ----
  async createClass(data: Record<string, unknown>): Promise<LiveClass> {
    return apiPost<LiveClass>('/live-classes', data);
  },

  async updateClass(id: number, data: UpdateLiveClassInput): Promise<LiveClass> {
    return apiPatch<LiveClass>(`/live-classes/${id}`, data);
  },

  async archiveClass(id: number): Promise<{ id: number; archived: boolean }> {
    return apiDelete<{ id: number; archived: boolean }>(`/live-classes/${id}`);
  },

  async createSessions(
    classId: number,
    sessions: Array<{
      title?: string | null;
      startsAt: string;
      endsAt: string;
      timezone?: string;
      capacity?: number | null;
      meetingProvider?: 'AGORA' | 'DAILY';
    }>
  ): Promise<LiveClassSession[]> {
    return apiPost<LiveClassSession[]>(`/live-classes/${classId}/sessions`, { sessions });
  },

  async updateSession(sessionId: number, data: UpdateSessionInput): Promise<LiveClassSession> {
    return apiPatch<LiveClassSession>(`/live-classes/sessions/${sessionId}`, data);
  },

  async cancelSession(sessionId: number): Promise<LiveClassSession> {
    return apiPost<LiveClassSession>(`/live-classes/sessions/${sessionId}/cancel`);
  },

  // ---- In-session streaming (Phase 2) ----
  async getJoinToken(sessionId: number): Promise<JoinTokenResponse> {
    return apiPost<JoinTokenResponse>(`/live-classes/sessions/${sessionId}/join-token`);
  },

  async startSession(sessionId: number): Promise<SessionStartResponse> {
    return apiPost<SessionStartResponse>(`/live-classes/sessions/${sessionId}/start`);
  },

  async endSession(sessionId: number): Promise<SessionEndResponse> {
    return apiPost<SessionEndResponse>(`/live-classes/sessions/${sessionId}/end`);
  },

  // ---- Real-time chat & Q&A (Phase 3) ----
  async getMessages(
    sessionId: number,
    opts?: { after?: number; limit?: number }
  ): Promise<MessagePageResponse> {
    const params: Record<string, unknown> = {};
    if (opts?.after) params.after = opts.after;
    if (opts?.limit) params.limit = opts.limit;
    return apiGet<MessagePageResponse>(`/live-classes/sessions/${sessionId}/messages`, params);
  },

  async sendMessage(sessionId: number, input: CreateMessageInput): Promise<LiveClassMessage> {
    return apiPost<LiveClassMessage>(`/live-classes/sessions/${sessionId}/messages`, input);
  },

  async voteMessage(messageId: number): Promise<{ id: number; votes: number }> {
    return apiPost<{ id: number; votes: number }>(`/live-classes/messages/${messageId}/vote`);
  },

  async moderateMessage(
    messageId: number,
    action: 'pin' | 'hide' | 'answer',
    opts?: { pinned?: boolean }
  ): Promise<LiveClassMessage> {
    if (action === 'pin') {
      return apiPatch<LiveClassMessage>(`/live-classes/messages/${messageId}/pin`, { pinned: opts?.pinned });
    }
    return apiPatch<LiveClassMessage>(`/live-classes/messages/${messageId}/${action}`);
  },

  async createPoll(sessionId: number, input: CreatePollInput): Promise<LiveClassMessage> {
    return apiPost<LiveClassMessage>(`/live-classes/sessions/${sessionId}/polls`, input);
  },

  async votePoll(
    sessionId: number,
    pollId: number,
    optionId: string
  ): Promise<PollResults> {
    return apiPost<PollResults>(`/live-classes/sessions/${sessionId}/polls/${pollId}/vote`, { optionId });
  },
};
